<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col class="d-flex">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
        </v-col>
        <v-col cols="auto" class="text-right">
          <v-btn
            :to="{
              name: department === 'organic' ? 'offers.ordering.performance' : 'offers.ordering.organic',
              query: { country: query.params.country, offerType: query.params.offerType },
            }"
            color="primary"
            depressed
          >
            <v-icon left>switch_access_shortcut</v-icon>
            <span>Switch to {{ department === 'organic' ? 'Performance' : 'Organic' }}</span>
          </v-btn>
          <v-btn
            :to="{
              name: 'offers.table-preview',
              query: { department, country: query.params.country, offerType: query.params.offerType },
            }"
            class="ml-4"
            color="blue"
            dark
            depressed
            >Table Preview</v-btn
          >
        </v-col>
      </v-row>
      <v-row class="mt-0">
        <v-col>
          <v-divider />
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row no-gutters>
        <v-col class="d-flex align-center justify-start">
          <select-list
            v-for="option in filterOptions"
            :key="option.name"
            v-model="query.params[option.model]"
            :custom-type="option.customType"
            :disabled="isLoading || hasChanges"
            :items="getItemsForOption(option.dataKey)"
            :label="option.name"
            :loading="isLoading"
            :placeholder="`Select ${option.name.toLowerCase()}`"
            class="mr-4"
            hide-details
          />
        </v-col>
        <v-col cols="auto" class="d-flex align-center justify-end">
          <search-input v-model="query.searchTerm" :disabled="hasPageErrors" :is-loading="isLoading" hide-details />
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row justify="space-between">
        <v-col sm="7">
          <offers-list
            v-if="orderedOffersUpdated.length"
            #default="{ index, offer }"
            v-model="orderedOffersUpdated"
            :is-disabled="isDisabled"
            draggable
            show-position
            highlight-draft
            show-star-rating
            show-deal-banner
          >
            <v-btn
              :disabled="isDisabled"
              small
              icon
              :to="{ name: 'offers.edit', params: { slug: offer.slug } }"
              class="mr-4"
            >
              <v-icon small>launch</v-icon>
            </v-btn>
            <v-btn :disabled="isDisabled" small icon @click.prevent="removeOffer(index)">
              <v-icon>delete_outline</v-icon>
            </v-btn>
          </offers-list>
          <v-card v-else class="pa-2 text-center" disabled outlined>Add an offer from those found on the right</v-card>
        </v-col>
        <v-col sm="5">
          <offers-list
            #default="{ offer }"
            :is-disabled="isDisabled"
            :items="filteredOffers"
            :pagination="pagination"
            show-star-rating
            show-deal-banner
            @page-change="onPageChange"
          >
            <v-btn
              :disabled="isDisabled"
              small
              icon
              :to="{ name: 'offers.edit', params: { slug: offer.slug } }"
              class="mr-4"
            >
              <v-icon small>launch</v-icon>
            </v-btn>
            <v-btn :disabled="isDisabled" small icon @click.prevent="addOffer(offer)">
              <v-icon>add</v-icon>
            </v-btn>
          </offers-list>
        </v-col>
      </v-row>
      <fab-actions
        :can-edit="isPermittedTo('update')"
        :has-changes="hasChanges"
        context="Order"
        @reset="showResetModal = true"
        @save="showSaveModal = true"
      />

      <reset-modal
        v-model="showResetModal"
        :is-disabled="isLoading"
        :is-loading="isLoading"
        type="order"
        @modal-confirm="onResetConfirm"
      />
      <ordering-modal
        v-model="showSaveModal"
        :is-disabled="isLoading"
        :is-loading="isLoading"
        :filters="query.params"
        :previous="orderedOffersOriginal"
        :current="orderedOffersUpdated"
        @modal-confirm="onSaveConfirm"
      />
      <exit-modal
        :is-disabled="isLoading"
        :is-loading="isLoading"
        :value="showExitModal"
        @modal-confirm="confirmExitBeforeSave"
        @modal-cancel="cancelExitBeforeSave"
      />
    </v-container>
  </page-loader>
</template>

<script>
import { merge, capitalize } from 'lodash-es'
import isSet from '@/utils/is-set'
import { mapGetters, mapMutations } from 'vuex'

import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { offers, deals } from '@/services/offers'

import FabActions from '@/components/fab-actions'
import OffersList from '@/views/offers/components/offers-list'
import OrderingModal from '@/components/modals/ordering-modal'
import PageLoader from '@/components/page-loader'
import ResetModal from '@/components/modals/reset-modal'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'

export default {
  name: 'OfferOrdering',
  components: {
    FabActions,
    OffersList,
    OrderingModal,
    PageLoader,
    ResetModal,
    SearchInput,
    SelectList,
  },
  mixins: [DataModification, PermissionsHandler, QueryParamsHandler],
  props: {
    department: {
      type: String,
      required: true,
      validator(value) {
        return ['organic', 'performance'].includes(value)
      },
    },
  },
  data: () => ({
    activeDeals: [],
    isLoading: false,
    queriesDefined: ['country', 'offerType'],
    queryDefaults: ['country', 'offerType'],
    queryPagination: false,
    querySearch: false,
    offers: [],
    orderedOffersOriginal: [],
    orderedOffersUpdated: [],
    filterOptions: [
      {
        name: 'Country',
        dataKey: 'countries',
        model: 'country',
        customType: 'country',
      },
      {
        name: 'Offer Type',
        dataKey: 'offerTypes',
        model: 'offerType',
      },
    ],
    showSaveModal: false,
    showResetModal: false,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    ...mapGetters('options', ['getItemsForOption']),
    breadcrumbs() {
      return [
        { text: 'Offers', to: { name: 'offers.management' }, exact: true },
        { text: 'Ordering', to: { name: 'offers.ordering' }, exact: true },
        { text: capitalize(this.department || 'loading'), disabled: true },
      ]
    },
    filteredDeals() {
      return this.activeDeals.reduce((acc, deal) => {
        if (deal.commission_rates[this.query.params.country]) {
          acc.push(Object.assign({}, deal, { commission_rates: deal.commission_rates[this.query.params.country] }))
        }
        return acc
      }, [])
    },
    filteredDealsByPartner() {
      return this.filteredDeals.reduce((acc, deal) => {
        acc[deal.partner] = deal
        return acc
      }, {})
    },
    filteredOffers() {
      const preOrderedOffers = this.offers
        .filter(({ slug }) => !this.orderedOffersUpdated.find(offer => offer.slug === slug))
        .filter(
          ({
            offer: {
              default: { departments = [] },
            },
          }) => departments && departments.includes(this.department)
        )
        .filter(
          ({
            offer: {
              default: { restrictedCountries = [] },
            },
          }) => restrictedCountries && !restrictedCountries.includes(this.query.params.country)
        )

      const filtered = this.filterArrBySearchQuery(preOrderedOffers)
      return filtered.map(this.mapOffer)
    },
    hasChanges() {
      return this.hasDataChanged(this.orderedOffersOriginal, this.orderedOffersUpdated)
    },
    isDisabled() {
      return this.isLoading || this.hasPageErrors || !this.isPermittedTo('update')
    },
    positionChanges() {
      const countryCode = this.query.params.country
      const { positionKey } = this
      const model = (position = null) => ({
        offer: {
          [countryCode]: {
            [positionKey]: position,
          },
        },
      })

      const changedPartners = this.orderedOffersUpdated.reduce((acc, { offer, slug }, index) => {
        const position = offer?.[countryCode]?.[positionKey]

        if (position === index) return acc

        acc[slug] = model(index)

        return acc
      }, {})

      const removedPartners = this.orderedOffersOriginal.reduce((acc, offer) => {
        const { slug } = offer
        const currentPosition = this.orderedOffersUpdated.findIndex(offer => offer.slug === slug)
        if (currentPosition !== -1) return acc

        acc[slug] = model(null)

        return acc
      }, {})

      return {
        ...changedPartners,
        ...removedPartners,
      }
    },
    positionKey() {
      return this.department === 'organic' ? 'position' : 'paidPosition'
    },
  },
  async created() {
    await this.getActiveDeals()
    await this.getFilteredTable()
    this.resetOffers()
    this.setBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getActiveDeals() {
      this.isLoading = true
      try {
        const { country, offerType } = this.query.params
        const filters = Object.entries({
          offer_typesContains: offerType,
          countriesContains: country,
          status: 'live',
        }).reduce((acc, [key, value]) => {
          if (isSet(value)) acc[key] = value
          return acc
        }, {})
        const { data } = await deals.getFilteredDeals({ filters })
        this.activeDeals = data
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve active deals',
        })
      }
      this.isLoading = false
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    addOffer(offer) {
      this.orderedOffersUpdated.push(offer)
    },
    removeOffer(index) {
      this.orderedOffersUpdated.splice(index, 1)
    },
    mapOffer({
      slug,
      type,
      partners,
      offer: {
        default: { name, logoThumbnail, starRating, published, restrictedCountries, departments },
      },
    }) {
      // Only include partner deal if no more than 1 partner is associated.
      const partner = partners?.length === 1 ? partners[0] : null
      const deal = partner ? this.filteredDealsByPartner[partner] : null
      const restricted =
        restrictedCountries && !!restrictedCountries.find(country => country === this.query.params.country)
      const restrictedForDepartment = !departments.includes(this.department)

      return {
        slug,
        type,
        name,
        logoThumbnail: logoThumbnail || null,
        starRating,
        published,
        deal,
        restricted,
        restrictedForDepartment,
      }
    },
    resetOffers() {
      const orderedOffers = this.getOrderedOffers()
      const mappedOffers = orderedOffers.map(this.mapOffer)
      this.orderedOffersOriginal = this.clone(mappedOffers)
      this.orderedOffersUpdated = this.clone(mappedOffers)
    },
    getOrderedOffers() {
      const { country } = this.query.params
      const { positionKey } = this
      return this.offers
        .filter(({ offer }) => offer?.[country]?.[positionKey] != undefined && offer?.[country]?.[positionKey] >= 0)
        .sort((a, b) => a.offer[country][positionKey] - b.offer[country][positionKey])
    },
    async getFilteredTable() {
      this.isLoading = true
      try {
        const { offerType } = this.query.params
        const { data } = await offers.getAllOffers({ filters: { type: offerType }, includePartners: true })
        this.offers = data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve offers',
        })
      }

      this.setPageLoading(false)
      this.isLoading = false
    },
    async onSaveConfirm() {
      const { country, offerType } = this.query.params
      this.isLoading = true

      const mergedOffers = Object.entries(this.positionChanges).map(([slug, data]) => {
        const offer = this.offers.find(offer => offer.slug === slug)
        delete offer.id
        delete offer.partners
        return merge({}, offer, data)
      })

      try {
        await offers.batchUpdateOffers({ offers: mergedOffers })
        this.$notify({
          type: 'success',
          text: `${country} ${offerType} table saved successfully`,
        })
        await this.getFilteredTable()
        this.resetOffers()
        this.showSaveModal = false
      } catch (error) {
        console.log(error)
        this.$notify({
          type: 'error',
          text: `Unable to update ${country} ${offerType} table`,
        })
      }

      this.isLoading = false
    },
    onResetConfirm() {
      this.resetOffers()
      this.$notify({
        type: 'success',
        text: 'Your changes have been reverted',
      })
      this.showResetModal = false
    },
    async handleQueryUpdate(queryType = null) {
      await this.getFilteredTable()
      await this.getActiveDeals()
      if (queryType === 'params') {
        this.resetOffers()
      }
    },
    async onPageChange(page) {
      this.pagination.currentPage = page
    },
  },
}
</script>
